import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Activity,
  Users,
  DollarSign,
  Server,
  TrendingUp,
  ArrowRight,
  Shield,
  FileText
} from 'lucide-react';

const BACKEND_URL = window.__BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminTile({
  icon: Icon,
  iconClassName,
  title,
  borderClass,
  clickable = false,
  onClick,
  showChevron = false,
  children
}) {
  const content = (
    <Card
      className={`glass h-full transition-all ${borderClass} ${
        clickable ? 'hover:bg-white/5 cursor-pointer' : ''
      }`}
    >
      <CardHeader>
        <CardTitle className="text-base flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon className={`h-5 w-5 ${iconClassName}`} />
            <span>{title}</span>
          </div>
          {showChevron && <ArrowRight className="h-4 w-4 text-[#8A93A6]" />}
        </CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );

  if (clickable && onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="block text-left h-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0A0F1C]"
      >
        {content}
      </button>
    );
  }

  return content;
}

export default function AdminOverview() {
  const navigate = useNavigate();
  const [systemHealth, setSystemHealth] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [valuation, setValuation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('ADMIN OVERVIEW ACTIVE: frontend/src/pages/new-app/AdminOverview.jsx');
    }
  }, []);

  useEffect(() => {
    fetchAdminData();
  }, []);

  const handleNavigate = (destination) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('AdminOverview click ->', destination);
    }
    navigate(destination);
  };

  const fetchAdminData = async () => {
    try {
      const healthRes = await fetch(`${API}/admin/system-health`, { credentials: 'include' });
      if (healthRes.ok) setSystemHealth(await healthRes.json());

      const logsRes = await fetch(`${API}/admin/activity-logs?limit=10`, { credentials: 'include' });
      if (logsRes.ok) {
        const data = await logsRes.json();
        setActivityLogs(data.logs || []);
      }

      const teamRes = await fetch(`${API}/admin/team-members`, { credentials: 'include' });
      if (teamRes.ok) {
        const data = await teamRes.json();
        setTeamMembers(data.members || []);
      }

      const valuationRes = await fetch(`${API}/admin/valuation`, { credentials: 'include' });
      if (valuationRes.ok) setValuation(await valuationRes.json());
    } catch (error) {
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getHealthColor = (status) => {
    if (status === 'healthy') return 'text-green-500';
    if (status === 'warning') return 'text-yellow-500';
    return 'text-red-500';
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6 max-w-7xl">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 max-w-7xl">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white mb-2">Admin Overview</h1>
        <p className="text-[#8A93A6]">Read-only governance & observation dashboard</p>
      </div>

      {/* Governance Tiles - Direct Links Only */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
        {/* System Health → System Settings */}
        <AdminTile
          icon={Server}
          iconClassName="text-blue-500"
          title="System Health"
          borderClass="border-blue-500/30"
          clickable
          showChevron
          onClick={() => handleNavigate('/app/admin/system-settings')}
        >
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#8A93A6]">Backend</span>
              <Badge variant="outline" className={getHealthColor(systemHealth?.backend_status || 'healthy')}>
                {systemHealth?.backend_status || 'Healthy'}
              </Badge>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#8A93A6]">Database</span>
              <Badge variant="outline" className={getHealthColor(systemHealth?.database_status || 'healthy')}>
                {systemHealth?.database_status || 'Healthy'}
              </Badge>
            </div>
          </div>
          <p className="text-xs text-[#8A93A6] mt-3">→ System Settings</p>
        </AdminTile>

        {/* Users → Read-only (not clickable) */}
        <AdminTile
          icon={Users}
          iconClassName="text-purple-500"
          title="Users"
          borderClass="border-purple-500/30"
        >
          <div className="text-3xl font-bold text-white">{systemHealth?.active_users || 0}</div>
          <p className="text-sm text-[#8A93A6]">Active users</p>
          <p className="text-xs text-[#8A93A6] mt-3">Read-only</p>
        </AdminTile>

        {/* Revenue → Read-only (not clickable) */}
        <AdminTile
          icon={DollarSign}
          iconClassName="text-green-500"
          title="Revenue"
          borderClass="border-green-500/30"
        >
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold text-white">
              {valuation?.mrr
                ? new Intl.NumberFormat('en-US', {
                    style: 'decimal',
                    minimumFractionDigits: 0
                  }).format(valuation.mrr)
                : '0'}
            </span>
            <span className="text-sm text-[#8A93A6]">$ MRR</span>
          </div>
          <p className="text-xs text-[#8A93A6] mt-3">Read-only</p>
        </AdminTile>

        {/* Valuation → Admin Valuation Page */}
        <AdminTile
          icon={TrendingUp}
          iconClassName="text-yellow-500"
          title="Valuation"
          borderClass="border-yellow-500/30"
          clickable
          showChevron
          onClick={() => handleNavigate('/app/admin/valuation')}
        >
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold text-white">
              {valuation?.mrr
                ? new Intl.NumberFormat('en-US', {
                    style: 'decimal',
                    minimumFractionDigits: 0
                  }).format((valuation.mrr || 0) * 12)
                : '0'}
            </span>
            <span className="text-sm text-[#8A93A6]">$ ARR</span>
          </div>
          <p className="text-xs text-[#8A93A6] mt-3">→ Valuation</p>
        </AdminTile>

        {/* Growth / Flywheel → Admin Growth */}
        <AdminTile
          icon={Activity}
          iconClassName="text-orange-500"
          title="Growth / Flywheel"
          borderClass="border-orange-500/30"
          clickable
          showChevron
          onClick={() => handleNavigate('/app/admin/growth')}
        >
          <p className="text-sm text-[#8A93A6]">Metrics visualization (read-only)</p>
          <p className="text-xs text-[#8A93A6] mt-3">Coming Soon</p>
        </AdminTile>

        {/* Activity Log → Direct link */}
        <AdminTile
          icon={FileText}
          iconClassName="text-pink-500"
          title="Activity"
          borderClass="border-pink-500/30"
          clickable
          showChevron
          onClick={() => handleNavigate('/app/admin/activity-log')}
        >
          <div className="text-3xl font-bold text-white">{activityLogs.length}</div>
          <p className="text-sm text-[#8A93A6]">Recent events</p>
          <p className="text-xs text-[#8A93A6] mt-3">→ Activity Log</p>
        </AdminTile>

        {/* Team → Direct link */}
        <AdminTile
          icon={Shield}
          iconClassName="text-indigo-500"
          title="Team & Permissions"
          borderClass="border-indigo-500/30"
          clickable
          showChevron
          onClick={() => handleNavigate('/app/admin/team-permissions')}
        >
          <div className="text-3xl font-bold text-white">{teamMembers.length}</div>
          <p className="text-sm text-[#8A93A6]">Team members</p>
          <p className="text-xs text-[#8A93A6] mt-3">→ Team & Permissions</p>
        </AdminTile>
      </div>
    </div>
  );
}
